import React, { useState } from 'react';
import styled from 'styled-components';
import { Plus, Trash2 } from 'lucide-react';
import { parseAmountShorthand, replaceShorthandOnBlur } from '../../../utils/amountUtils';

/**
 * FR-1.5 — the contract's payment waterfall, lender classes and consequences,
 * entered as structure rather than as JSON.
 *
 * Every option offered here is one the engines actually honour: the payment
 * types the waterfall can compute, the two consequences the funding and
 * waterfall controls read, the three node tests that gate a payment. Offering
 * more would be promising a control that does not exist.
 */

export type WaterfallMode = 'PRE_TRIGGER' | 'POST_TRIGGER' | 'BOTH';

export interface WaterfallNodeDraft {
  priorityOrder: string;
  nodeName: string;
  paymentType: string;
  mode: WaterfallMode;
  rateBps: string;
  fixedAmount: string;
  capAmount: string;
  lenderClassKey: string;
  destinationAccountKey: string;
  testType: string;
  testThreshold: string;
  onFailureAction: string;
  sourceClause: string;
}

export interface LenderClassDraft {
  classKey: string;
  className: string;
  seniority: string;
  priorityOrder: string;
  commitmentAmount: string;
  currency: string;
  rateIndex: string;
  spreadBps: string;
  sourceClause: string;
}

export interface ConsequenceDraft {
  consequenceKey: string;
  consequenceType: string;
  targetType: string;
  targetKey: string;
  /** F-S6-024: the covenant whose breach triggers this consequence (recorded on the covenant as its
   *  breach effect), distinct from the target the consequence acts on (`DRAW` for a draw block). */
  covenantKey: string;
  sourceClause: string;
}

export interface ContractSchedulesDraft {
  waterfallNodes: WaterfallNodeDraft[];
  lenderClasses: LenderClassDraft[];
  consequences: ConsequenceDraft[];
}

export const emptyContractSchedules = (): ContractSchedulesDraft => ({
  waterfallNodes: [], lenderClasses: [], consequences: [],
});

const emptyNode = (priority: number): WaterfallNodeDraft => ({
  priorityOrder: String(priority), nodeName: '', paymentType: '', mode: 'PRE_TRIGGER',
  rateBps: '', fixedAmount: '', capAmount: '', lenderClassKey: '', destinationAccountKey: '',
  testType: '', testThreshold: '', onFailureAction: '', sourceClause: '',
});

const emptyLenderClass = (): LenderClassDraft => ({
  classKey: '', className: '', seniority: '', priorityOrder: '', commitmentAmount: '',
  currency: 'USD', rateIndex: '', spreadBps: '', sourceClause: '',
});

const emptyConsequence = (): ConsequenceDraft => ({
  consequenceKey: '', consequenceType: '', targetType: '', targetKey: '', covenantKey: '', sourceClause: '',
});

/** What the waterfall engine can compute, and what each line is paid from. */
const PAYMENT_TYPES: { value: string; label: string; needs?: 'rate' | 'fixed' | 'cap' }[] = [
  { value: 'FEE_RATE_COLLATERAL', label: 'Fee on eligible collateral', needs: 'rate' },
  { value: 'FEE_RATE_UNDRAWN', label: 'Fee on the undrawn commitment', needs: 'rate' },
  { value: 'FEE_FIXED', label: 'Fixed fee', needs: 'fixed' },
  { value: 'INTEREST_ON_DRAWN', label: 'Interest on the drawn balance' },
  { value: 'PRINCIPAL', label: 'Principal collections' },
  { value: 'SWEEP', label: 'Full sweep of period collections' },
  { value: 'LENDER_DISTRIBUTION', label: 'Distribution to a lender class', needs: 'fixed' },
  { value: 'RESERVE_FUND', label: 'Reserve top-up', needs: 'cap' },
  { value: 'RESIDUAL', label: 'Residual to the borrower' },
];

const MODES: { value: WaterfallMode; label: string }[] = [
  { value: 'PRE_TRIGGER', label: 'Normal (no trigger in force)' },
  { value: 'POST_TRIGGER', label: 'After an amortisation trigger' },
  { value: 'BOTH', label: 'Both' },
];

const TEST_TYPES = [
  { value: '', label: 'No test — this line always pays' },
  { value: 'ADVANCE_RATE_MAX', label: 'Advance rate at or below (%)' },
  { value: 'DELINQUENCY_MAX', label: 'Delinquency at or below (%)' },
  { value: 'NET_LOSS_MAX', label: 'Net loss rate at or below (%)' },
];

const FAILURE_ACTIONS = [
  { value: 'TRAP_TO_RESERVE', label: 'Trap remaining cash to the reserve' },
  { value: 'BLOCK', label: 'Stop the waterfall here' },
  { value: 'DIVERT_TO_PRINCIPAL', label: 'Skip this line, divert cash to principal' },
];

const CONSEQUENCE_TYPES = [
  { value: 'DRAW_BLOCK', label: 'Block further draws' },
  { value: 'WATERFALL_MODE', label: 'Switch the waterfall to post-trigger' },
];

const TARGET_TYPES = [
  { value: 'FACILITY', label: 'The facility' },
  { value: 'WATERFALL', label: 'The payment waterfall' },
];

const SENIORITIES = ['SENIOR', 'MEZZANINE', 'SUBORDINATED', 'EQUITY'];

const numeric = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed.replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

/** F-S6-019: money boxes take the platform shorthand (50MM, 500K); empty or unparseable is null. */
const money = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = parseAmountShorthand(trimmed);
  return parsed > 0 ? parsed : numeric(trimmed);
};

const str = (value: unknown): string => value === null || value === undefined ? '' : String(value);

/** F-S6-020: the payees a waterfall line can name without typing a key. */
export const FIXED_PAYEES: { key: string; label: string }[] = [
  { key: 'SERVICER', label: 'Servicer (fee)' },
  { key: 'ADMIN_FEE', label: 'Administrative agent (fee)' },
  { key: 'RESERVE', label: 'Reserve account' },
  { key: 'BORROWER_RESIDUAL', label: 'Borrower (residual)' },
];
const OTHER = '__OTHER__';
/** Walkthrough 2026-09-22: the indices the platform prices; the same vocabulary as the facility's financing index. */
const RATE_INDICES: { value: string; label: string }[] = [
  { value: 'SOFR', label: 'SOFR' },
  { value: 'TERM_SOFR', label: 'Term SOFR' },
  { value: 'PRIME', label: 'Prime' },
  { value: 'EURIBOR', label: 'EURIBOR' },
  { value: 'SONIA', label: 'SONIA' },
  { value: 'FIXED', label: 'Fixed rate' },
];

/**
 * Slice 6 FR-6.2: prefill the editor from the active version's materialised
 * schedules, so a maker changing one line sees the other lines carried.
 */
type LooseRecord = Record<string, unknown>;
// Accepts both the raw extraction rows and the typed FacilityContractVersion
// records (interfaces carry no index signature, so they are widened here).
export const contractSchedulesFromVersion = (input: {
  waterfallNodes?: ReadonlyArray<object>;
  lenderClasses?: ReadonlyArray<object>;
  consequences?: ReadonlyArray<object>;
  covenants?: ReadonlyArray<object>;
}): ContractSchedulesDraft => {
  const version = input as { waterfallNodes?: LooseRecord[]; lenderClasses?: LooseRecord[]; consequences?: LooseRecord[]; covenants?: LooseRecord[] };
  const triggerOf = (consequenceKey: unknown) => str((version.covenants || [])
    .find(covenant => str(covenant.breach_effect_key) === str(consequenceKey))?.covenant_key);
  return ({
  waterfallNodes: (version.waterfallNodes || []).filter(node => node.enabled !== false).map(node => ({
    priorityOrder: str(node.priority_order), nodeName: str(node.node_name), paymentType: str(node.payment_type),
    mode: (str(node.mode) || 'PRE_TRIGGER') as WaterfallMode, rateBps: str(node.rate_bps), fixedAmount: str(node.fixed_amount),
    capAmount: str(node.cap_amount), lenderClassKey: str(node.lender_class_key), destinationAccountKey: str(node.destination_account_key),
    testType: str(node.test_type), testThreshold: str(node.test_threshold), onFailureAction: str(node.on_failure_action),
    sourceClause: str(node.source_clause),
  })),
  lenderClasses: (version.lenderClasses || []).map(lender => ({
    classKey: str(lender.class_key), className: str(lender.class_name), seniority: str(lender.seniority),
    priorityOrder: str(lender.priority_order), commitmentAmount: str(lender.commitment_amount),
    currency: str(lender.currency) || 'USD', rateIndex: str(lender.rate_index), spreadBps: str(lender.spread_bps),
    sourceClause: str(lender.source_clause),
  })),
  consequences: (version.consequences || []).map(consequence => ({
    consequenceKey: str(consequence.consequence_key), consequenceType: str(consequence.consequence_type),
    targetType: str(consequence.target_type), targetKey: str(consequence.target_key),
    covenantKey: triggerOf(consequence.consequence_key), sourceClause: str(consequence.source_clause),
  })),
  });
};

export const validateContractSchedules = (draft: ContractSchedulesDraft): string | null => {
  const priorities = new Map<string, Set<number>>();
  for (let index = 0; index < draft.waterfallNodes.length; index += 1) {
    const node = draft.waterfallNodes[index];
    const label = `Waterfall line ${index + 1}`;
    const priority = numeric(node.priorityOrder);
    if (priority == null || priority < 1 || !Number.isInteger(priority)) {
      return `${label} needs a whole pay-order number of 1 or more`;
    }
    const seen = priorities.get(node.mode) || new Set<number>();
    if (seen.has(priority)) return `${label} repeats pay order ${priority} in the same waterfall`;
    seen.add(priority);
    priorities.set(node.mode, seen);
    if (!node.nodeName.trim()) return `${label} needs a name`;
    if (!node.paymentType) return `${label} needs a payment type`;
    if (!node.sourceClause.trim()) return `${label} needs the clause it comes from`;
    const needs = PAYMENT_TYPES.find(type => type.value === node.paymentType)?.needs;
    if (needs === 'rate' && numeric(node.rateBps) == null) return `${label} needs its rate in bps`;
    if (needs === 'fixed' && money(node.fixedAmount) == null) return `${label} needs its amount`;
    if (node.testType && numeric(node.testThreshold) == null) {
      return `${label} has a test with no threshold`;
    }
    if (node.testType && !node.onFailureAction) {
      return `${label} has a test but does not say what happens when it fails`;
    }
    if (!node.testType && (node.testThreshold.trim() || node.onFailureAction)) {
      return `${label} has a threshold or failure action with no test`;
    }
  }
  const classKeys = new Set<string>();
  for (let index = 0; index < draft.lenderClasses.length; index += 1) {
    const lender = draft.lenderClasses[index];
    const label = `Lender class ${index + 1}`;
    const key = lender.classKey.trim().toUpperCase();
    if (!key) return `${label} needs a key`;
    if (classKeys.has(key)) return `${label} repeats the key ${key}`;
    classKeys.add(key);
    if (!lender.className.trim()) return `${label} needs a name`;
    if (money(lender.commitmentAmount) == null) return `${label} needs its commitment`;
    if (!lender.sourceClause.trim()) return `${label} needs the clause it comes from`;
  }
  const consequenceKeys = new Set<string>();
  for (let index = 0; index < draft.consequences.length; index += 1) {
    const consequence = draft.consequences[index];
    const label = `Consequence ${index + 1}`;
    const key = consequence.consequenceKey.trim().toUpperCase();
    if (!key) return `${label} needs a key`;
    if (consequenceKeys.has(key)) return `${label} repeats the key ${key}`;
    consequenceKeys.add(key);
    if (!consequence.consequenceType) return `${label} needs a type`;
    if (!consequence.targetType) return `${label} needs a target`;
    if (!consequence.sourceClause.trim()) return `${label} needs the clause it comes from`;
  }
  const lines = draft.waterfallNodes.filter(node => node.paymentType === 'LENDER_DISTRIBUTION');
  for (const line of lines) {
    const key = line.lenderClassKey.trim().toUpperCase();
    if (key && !classKeys.has(key)) {
      return `Waterfall line "${line.nodeName || key}" pays a lender class that is not defined`;
    }
  }
  return null;
};

export const contractSchedulesPayload = (draft: ContractSchedulesDraft) => ({
  waterfallNodes: draft.waterfallNodes.map(node => ({
    priorityOrder: numeric(node.priorityOrder),
    nodeName: node.nodeName.trim(),
    paymentType: node.paymentType,
    mode: node.mode,
    rateBps: numeric(node.rateBps),
    fixedAmount: money(node.fixedAmount),
    capAmount: money(node.capAmount),
    lenderClassKey: node.lenderClassKey.trim().toUpperCase() || null,
    destinationAccountKey: node.destinationAccountKey.trim().toUpperCase() || null,
    testType: node.testType || null,
    testThreshold: node.testType ? numeric(node.testThreshold) : null,
    onFailureAction: node.testType ? node.onFailureAction || null : null,
    sourceClause: node.sourceClause.trim(),
  })),
  lenderClasses: draft.lenderClasses.map(lender => ({
    classKey: lender.classKey.trim().toUpperCase(),
    className: lender.className.trim(),
    seniority: lender.seniority || null,
    priorityOrder: numeric(lender.priorityOrder),
    commitmentAmount: money(lender.commitmentAmount),
    currency: lender.currency.trim().toUpperCase() || null,
    rateIndex: lender.rateIndex.trim().toUpperCase() || null,
    spreadBps: numeric(lender.spreadBps),
    sourceClause: lender.sourceClause.trim(),
  })),
  consequences: draft.consequences.map(consequence => ({
    consequenceKey: consequence.consequenceKey.trim().toUpperCase(),
    consequenceType: consequence.consequenceType,
    targetType: consequence.targetType,
    targetKey: consequence.targetKey.trim().toUpperCase() || null,
    covenantKey: consequence.covenantKey.trim().toUpperCase() || null,
    sourceClause: consequence.sourceClause.trim(),
  })),
});

const AddRow = styled.div`display: flex; justify-content: flex-start; padding-top: .2rem;`;
const Wrap = styled.section`
  display: grid;
  gap: .6rem;
`;
const Group = styled.div`
  padding: .7rem;
  border: 1px solid rgba(148,163,184,.16);
  border-radius: 10px;
  background: rgba(15,23,42,.5);
`;
const GroupHead = styled.div`
  display: flex; justify-content: space-between; gap: 1rem; align-items: flex-start;
  h4 { margin: 0; color: #e2e8f0; font-size: .76rem; }
  p { margin: .2rem 0 .6rem; max-width: 44rem; color: #94a3b8; font-size: .66rem; line-height: 1.5; }
`;
const Row = styled.div`
  margin-top: .5rem; padding: .6rem; border: 1px solid rgba(148,163,184,.14);
  border-radius: 9px; background: rgba(2,6,23,.5); display: grid; gap: .45rem;
`;
const RowHead = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: .6rem;
  strong { color: #cbd5e1; font-size: .68rem; }
`;
const Fields = styled.div`
  display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: .45rem; align-items: end;
  @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0,1fr)); }
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;
const Label = styled.label`
  display: flex; flex-direction: column; gap: .22rem; min-width: 0;
  color: #94a3b8; font-size: .61rem;
  input, select { min-width: 0; box-sizing: border-box; width: 100%; height: 2.2rem;
    padding: .45rem .5rem; border: 1px solid rgba(148,163,184,.2); border-radius: 7px;
    background: #0f172a; color: #e2e8f0; font-family: inherit; font-size: .68rem; }
`;
const Button = styled.button`
  display: inline-flex; gap: .3rem; align-items: center; border: 1px solid rgba(56,189,248,.25);
  border-radius: 7px; padding: .4rem .55rem; background: rgba(14,165,233,.1); color: #bae6fd;
  font-size: .64rem; cursor: pointer;
`;
const Empty = styled.p`
  margin: .2rem 0 0; color: #64748b; font-size: .66rem; line-height: 1.5;
`;

interface Props {
  value: ContractSchedulesDraft;
  onChange: (value: ContractSchedulesDraft) => void;
  /** Onboarding states the schedules; an amendment changes what is already live. */
  lane?: 'NEW_FACILITY' | 'AMENDMENT';
  /** F-S6-020: the account keys the version holds (in force, or drafted in the same form), offered as payees. */
  accountKeys?: string[];
  /** F-S6-020: the version's covenants, offered as the trigger of a consequence. */
  covenants?: { key: string; name: string }[];
}

const ContractSchedulesEditor: React.FC<Props> = ({ value, onChange, lane = 'AMENDMENT', accountKeys = [], covenants = [] }) => {
  const onboarding = lane === 'NEW_FACILITY';
  // Lines and consequences whose key is typed rather than picked (the explicit "Other…").
  const [otherPayee, setOtherPayee] = useState<Set<number>>(new Set());
  const [otherTrigger, setOtherTrigger] = useState<Set<number>>(new Set());
  const toggle = (set: Set<number>, index: number, on: boolean) => { const next = new Set(set); if (on) next.add(index); else next.delete(index); return next; };
  const classPayees = value.lenderClasses.filter(lender => lender.classKey.trim())
    .map(lender => ({ key: `${lender.classKey.trim().toUpperCase()}_LENDER`, label: `${lender.className || lender.classKey} (lender class)` }));
  const payees = [
    ...FIXED_PAYEES,
    ...classPayees,
    ...accountKeys.filter(key => key && !FIXED_PAYEES.some(p => p.key === key)).map(key => ({ key, label: `${key} (account)` })),
  ].filter((payee, index, all) => all.findIndex(other => other.key === payee.key) === index);
  const updateNode = (index: number, patch: Partial<WaterfallNodeDraft>) => onChange({
    ...value,
    waterfallNodes: value.waterfallNodes.map((node, i) => i === index ? { ...node, ...patch } : node),
  });
  const updateLender = (index: number, patch: Partial<LenderClassDraft>) => onChange({
    ...value,
    lenderClasses: value.lenderClasses.map((item, i) => i === index ? { ...item, ...patch } : item),
  });
  const updateConsequence = (index: number, patch: Partial<ConsequenceDraft>) => onChange({
    ...value,
    consequences: value.consequences.map((item, i) => i === index ? { ...item, ...patch } : item),
  });

  return (
    <Wrap>
      <Group>
        <GroupHead>
          <div>
            <h4>Lender classes</h4>
            <p>The tranches the agreement pays, with the commitment each one holds.</p>
          </div>

        </GroupHead>
        {value.lenderClasses.length === 0 && (
          <Empty>{onboarding
            ? 'No lender classes entered. A single-lender facility needs none.'
            : 'No lender classes in this amendment. A single-lender facility needs none.'}</Empty>
        )}
        {value.lenderClasses.map((lender, index) => (
          <Row key={`lender-${index}`}>
            <RowHead>
              <strong>Class {index + 1}</strong>
              <Button type="button" onClick={() => onChange({
                ...value, lenderClasses: value.lenderClasses.filter((_, i) => i !== index),
              })}><Trash2 size={12} /> Remove</Button>
            </RowHead>
            <Fields>
              <Label>Key
                <input value={lender.classKey} placeholder="CLASS_A"
                  onChange={e => updateLender(index, { classKey: e.target.value })} />
              </Label>
              <Label>Name
                <input value={lender.className} placeholder="Class A revolving lenders"
                  onChange={e => updateLender(index, { className: e.target.value })} />
              </Label>
              <Label>Seniority
                <select value={lender.seniority}
                  onChange={e => updateLender(index, { seniority: e.target.value })}>
                  <option value="">Not stated</option>
                  {SENIORITIES.map(rank => <option key={rank}>{rank}</option>)}
                </select>
              </Label>
              <Label>Pay order
                <input type="number" step="1" value={lender.priorityOrder}
                  onChange={e => updateLender(index, { priorityOrder: e.target.value })} />
              </Label>
              <Label>Commitment
                <input inputMode="decimal" placeholder="e.g. 50MM" value={lender.commitmentAmount}
                  onChange={e => updateLender(index, { commitmentAmount: e.target.value })}
                  onBlur={e => updateLender(index, { commitmentAmount: replaceShorthandOnBlur(e.target.value) })} />
              </Label>
              <Label>Currency
                <input value={lender.currency}
                  onChange={e => updateLender(index, { currency: e.target.value })} />
              </Label>
              <Label>Rate index (optional)
                <select value={RATE_INDICES.some(option => option.value === lender.rateIndex.trim().toUpperCase()) ? lender.rateIndex.trim().toUpperCase() : lender.rateIndex ? OTHER : ''}
                  onChange={e => updateLender(index, { rateIndex: e.target.value === OTHER ? lender.rateIndex : e.target.value })}>
                  <option value="">Not stated</option>
                  {RATE_INDICES.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                  {lender.rateIndex && !RATE_INDICES.some(option => option.value === lender.rateIndex.trim().toUpperCase()) && <option value={OTHER}>{lender.rateIndex.trim()}</option>}
                </select>
              </Label>
              <Label>Spread (bps, optional)
                <input type="number" step="any" value={lender.spreadBps}
                  onChange={e => updateLender(index, { spreadBps: e.target.value })} />
              </Label>
              <Label>Source clause
                <input value={lender.sourceClause} placeholder="Schedule II"
                  onChange={e => updateLender(index, { sourceClause: e.target.value })} />
              </Label>
            </Fields>
          </Row>
        ))}
        <AddRow><Button type="button" onClick={() => onChange({
            ...value, lenderClasses: [...value.lenderClasses, emptyLenderClass()],
          })}><Plus size={12} /> Add a class</Button></AddRow>
      </Group>

      <Group>
        <GroupHead>
          <div>
            <h4>Payment waterfall</h4>
            <p>
              The order the agreement pays collections in. Each line says what it pays,
              what it is paid from, and — where the agreement gates it — the test that must
              hold before it pays and what happens to the cash when it does not.
            </p>
          </div>

        </GroupHead>
        {value.waterfallNodes.length === 0 && (
          <Empty>{onboarding
            ? 'No waterfall entered. Enter the agreement\u2019s priority of payments, or leave it empty if the agreement states none.'
            : 'No waterfall in this amendment. The active version\u2019s waterfall continues to apply.'}</Empty>
        )}
        {value.waterfallNodes.map((node, index) => {
          const needs = PAYMENT_TYPES.find(type => type.value === node.paymentType)?.needs;
          return (
            <Row key={`node-${index}`}>
              <RowHead>
                <strong>Line {index + 1}</strong>
                <Button type="button" onClick={() => onChange({
                  ...value, waterfallNodes: value.waterfallNodes.filter((_, i) => i !== index),
                })}><Trash2 size={12} /> Remove</Button>
              </RowHead>
              <Fields>
                <Label>Pay order
                  <input type="number" step="1" min="1" value={node.priorityOrder}
                    onChange={e => updateNode(index, { priorityOrder: e.target.value })} />
                </Label>
                <Label>Name
                  <input value={node.nodeName} placeholder="Servicing fee"
                    onChange={e => updateNode(index, { nodeName: e.target.value })} />
                </Label>
                <Label>Pays
                  <select value={node.paymentType}
                    onChange={e => updateNode(index, { paymentType: e.target.value })}>
                    <option value="">Select…</option>
                    {PAYMENT_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </Label>
                <Label>Applies
                  <select value={node.mode}
                    onChange={e => updateNode(index, { mode: e.target.value as WaterfallMode })}>
                    {MODES.map(mode => <option key={mode.value} value={mode.value}>{mode.label}</option>)}
                  </select>
                </Label>
                {needs === 'rate' && (
                  <Label>Rate (bps per year)
                    <input type="number" step="any" value={node.rateBps}
                      onChange={e => updateNode(index, { rateBps: e.target.value })} />
                  </Label>
                )}
                {needs === 'fixed' && (
                  <Label>Amount per period
                    <input inputMode="decimal" placeholder="e.g. 250K" value={node.fixedAmount}
                      onChange={e => updateNode(index, { fixedAmount: e.target.value })}
                      onBlur={e => updateNode(index, { fixedAmount: replaceShorthandOnBlur(e.target.value) })} />
                  </Label>
                )}
                <Label>{needs === 'cap' ? 'Reserve target' : 'Cap per period (optional)'}
                  <input inputMode="decimal" placeholder="e.g. 500K" value={node.capAmount}
                    onChange={e => updateNode(index, { capAmount: e.target.value })}
                    onBlur={e => updateNode(index, { capAmount: replaceShorthandOnBlur(e.target.value) })} />
                </Label>
                {/* F-S6-018: a class may be named on any line (interest, principal, sweep pay a class);
                    it is required only for a distribution line. Offered once a class exists in the form. */}
                {(node.paymentType === 'LENDER_DISTRIBUTION' || value.lenderClasses.some(lender => lender.classKey.trim())) && (
                  <Label>{node.paymentType === 'LENDER_DISTRIBUTION' ? 'Lender class' : 'Lender class (optional)'}
                    <select value={node.lenderClassKey}
                      onChange={e => updateNode(index, { lenderClassKey: e.target.value })}>
                      <option value="">{node.paymentType === 'LENDER_DISTRIBUTION' ? 'Select…' : 'None — pays the facility'}</option>
                      {value.lenderClasses.filter(lender => lender.classKey.trim()).map(lender => (
                        <option key={lender.classKey} value={lender.classKey.trim().toUpperCase()}>
                          {lender.className || lender.classKey}
                        </option>
                      ))}
                    </select>
                  </Label>
                )}
                {(() => {
                  const key = node.destinationAccountKey.trim().toUpperCase();
                  const listed = payees.some(p => p.key === key);
                  const other = otherPayee.has(index) || (key !== '' && !listed);
                  return <>
                    <Label>Pays to (optional)
                      <select value={other ? OTHER : key}
                        onChange={e => {
                          if (e.target.value === OTHER) { setOtherPayee(toggle(otherPayee, index, true)); updateNode(index, { destinationAccountKey: '' }); }
                          else { setOtherPayee(toggle(otherPayee, index, false)); updateNode(index, { destinationAccountKey: e.target.value }); }
                        }}>
                        <option value="">Not stated</option>
                        {payees.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                        <option value={OTHER}>Other… (type a key)</option>
                      </select>
                    </Label>
                    {other && <Label>Payee key
                      <input value={node.destinationAccountKey} placeholder="e.g. TRUSTEE"
                        onChange={e => updateNode(index, { destinationAccountKey: e.target.value })} />
                    </Label>}
                  </>;
                })()}
                <Label>Test before paying
                  <select value={node.testType}
                    onChange={e => updateNode(index, {
                      testType: e.target.value,
                      ...(e.target.value ? {} : { testThreshold: '', onFailureAction: '' }),
                    })}>
                    {TEST_TYPES.map(test => (
                      <option key={test.value || 'none'} value={test.value}>{test.label}</option>
                    ))}
                  </select>
                </Label>
                {node.testType && (
                  <>
                    <Label>Threshold
                      <input type="number" step="any" value={node.testThreshold}
                        onChange={e => updateNode(index, { testThreshold: e.target.value })} />
                    </Label>
                    <Label>When the test fails
                      <select value={node.onFailureAction}
                        onChange={e => updateNode(index, { onFailureAction: e.target.value })}>
                        <option value="">Select…</option>
                        {FAILURE_ACTIONS.map(action => (
                          <option key={action.value} value={action.value}>{action.label}</option>
                        ))}
                      </select>
                    </Label>
                  </>
                )}
                <Label>Source clause
                  <input value={node.sourceClause} placeholder="Section 6.02(a)"
                    onChange={e => updateNode(index, { sourceClause: e.target.value })} />
                </Label>
              </Fields>
            </Row>
          );
        })}
        <AddRow><Button type="button" onClick={() => onChange({
            ...value,
            waterfallNodes: [...value.waterfallNodes, emptyNode((value.waterfallNodes.length + 1) * 10)],
          })}><Plus size={12} /> Add a line</Button></AddRow>
      </Group>

      <Group>
        <GroupHead>
          <div>
            <h4>Consequences</h4>
            <p>
              What the agreement does when a covenant or trigger fails. Only the two the
              platform enforces are offered: blocking further draws, and switching the
              waterfall to its post-trigger order.
            </p>
          </div>

        </GroupHead>
        {value.consequences.length === 0 && (
          <Empty>{onboarding
            ? 'No consequences entered. Add one for each test the agreement acts on.'
            : 'No consequences in this amendment.'}</Empty>
        )}
        {value.consequences.map((consequence, index) => (
          <Row key={`consequence-${index}`}>
            <RowHead>
              <strong>Consequence {index + 1}</strong>
              <Button type="button" onClick={() => onChange({
                ...value, consequences: value.consequences.filter((_, i) => i !== index),
              })}><Trash2 size={12} /> Remove</Button>
            </RowHead>
            <Fields>
              <Label>Key
                <input value={consequence.consequenceKey} placeholder="AMORT_EVENT"
                  onChange={e => updateConsequence(index, { consequenceKey: e.target.value })} />
              </Label>
              <Label>Does
                <select value={consequence.consequenceType}
                  onChange={e => updateConsequence(index, {
                    consequenceType: e.target.value,
                    ...(consequence.targetKey ? {} : e.target.value === 'DRAW_BLOCK' ? { targetType: consequence.targetType || 'FACILITY', targetKey: 'DRAW' }
                      : e.target.value === 'WATERFALL_MODE' ? { targetType: consequence.targetType || 'WATERFALL', targetKey: 'WATERFALL' } : {}),
                  })}>
                  <option value="">Select…</option>
                  {CONSEQUENCE_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </Label>
              <Label>Applies to
                <select value={consequence.targetType}
                  onChange={e => updateConsequence(index, { targetType: e.target.value })}>
                  <option value="">Select…</option>
                  {TARGET_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </Label>
              <Label>Acts on
                <select value={['DRAW', 'WATERFALL'].includes(consequence.targetKey.trim().toUpperCase()) || !consequence.targetKey ? consequence.targetKey.trim().toUpperCase() : OTHER}
                  onChange={e => updateConsequence(index, { targetKey: e.target.value === OTHER ? consequence.targetKey : e.target.value })}>
                  <option value="">Select…</option>
                  <option value="DRAW">New draws</option>
                  <option value="WATERFALL">The waterfall order</option>
                  {consequence.targetKey && !['DRAW', 'WATERFALL'].includes(consequence.targetKey.trim().toUpperCase()) && <option value={OTHER}>{consequence.targetKey}</option>}
                </select>
              </Label>
              {(() => {
                const key = consequence.covenantKey.trim().toUpperCase();
                const listed = covenants.some(c => c.key === key);
                const other = otherTrigger.has(index) || (key !== '' && !listed);
                return <>
                  <Label>Triggered by (optional)
                    <select value={other ? OTHER : key}
                      onChange={e => {
                        if (e.target.value === OTHER) { setOtherTrigger(toggle(otherTrigger, index, true)); updateConsequence(index, { covenantKey: '' }); }
                        else { setOtherTrigger(toggle(otherTrigger, index, false)); updateConsequence(index, { covenantKey: e.target.value }); }
                      }}>
                      <option value="">Not stated</option>
                      {covenants.map(c => <option key={c.key} value={c.key}>{c.name || c.key}</option>)}
                      <option value={OTHER}>Other… (type a covenant key)</option>
                    </select>
                  </Label>
                  {other && <Label>Covenant key
                    <input value={consequence.covenantKey} placeholder="e.g. EXCESS_AVAILABILITY"
                      onChange={e => updateConsequence(index, { covenantKey: e.target.value })} />
                  </Label>}
                </>;
              })()}
              <Label>Source clause
                <input value={consequence.sourceClause} placeholder="Section 7.01"
                  onChange={e => updateConsequence(index, { sourceClause: e.target.value })} />
              </Label>
            </Fields>
          </Row>
        ))}
        <AddRow><Button type="button" onClick={() => onChange({
            ...value, consequences: [...value.consequences, emptyConsequence()],
          })}><Plus size={12} /> Add a consequence</Button></AddRow>
      </Group>
    </Wrap>
  );
};

export default ContractSchedulesEditor;
